import { Button, Card, Col, Grid, Form, Input, InputNumber, Row, Space, Typography, Divider, Alert } from "antd";
import React, { useContext } from "react";
import { ConfiguratorContext } from "../context";
import { setQueryStringValuesWithoutNavigation } from "../../../utils/router";
import PageHeader from "../../../components/UI/PageHeader";
import { UndoOutlined } from "@ant-design/icons";
import ProductCard from "../../../components/Product/ProductCard";
import { Trans, useTranslation } from "react-i18next";

const { useBreakpoint } = Grid;

const SizeForm = ({ onFieldChange, onSubmit, product, type, form, t }) => {

    const minWidth = product[`min_${type}_width`] || 0;
    const maxWidth = product[`max_${type}_width`] || 0;
    const meanWidth = (minWidth + maxWidth) / 2;

    const minHeight = product[`min_${type}_height`] || 0;
    const maxHeight = product[`max_${type}_height`] || 0;
    const meanHeight = (minHeight + maxHeight) / 2;

    const getNumericFieldRules = (min: number, max: number): any[] => {
        return [
            {
                required: true,
                message: t('configurator.required_field_error')
            },
            {
                min: min,
                type: 'number',
                message: t('configurator.min_number_error', {
                    min: min
                })
            },
            {
                max: max,
                type: 'number',
                message: t('configurator.max_number_error', {
                    max: max
                })
            }
        ]
    }

    return (
        <Form
            layout="vertical"
            onFinish={onSubmit}
            initialValues={
                {
                    measureType: type,
                    width: meanWidth,
                    height: meanHeight
                }
            }
            form={form}
            onFieldsChange={(changedFields) => {
                onFieldChange({
                    name: changedFields[0]?.name[0],
                    value: changedFields[0]?.value,
                    form: type
                });
            }}
        >

            <Form.Item noStyle name="measureType">
                <Input type="hidden" />
            </Form.Item>

            <Form.Item
                rules={getNumericFieldRules(minWidth, maxWidth)}
                label={t(`configurator.width_${type}`)}
                name="width"
            >
                <InputNumber
                    addonAfter="mm"
                />
            </Form.Item>

            <Form.Item
                rules={getNumericFieldRules(minHeight, maxHeight)}
                label={t(`configurator.height_${type}`)}
                name="height"
            >
                <InputNumber
                    addonAfter='mm'
                />
            </Form.Item>

            <Form.Item>
                <Button type="primary" htmlType="submit" size="large" >
                    {t('configurator.select')}
                </Button>
            </Form.Item>


        </Form>
    )
}

const MeasureStep = () => {

    const { t } = useTranslation();

    const screens = useBreakpoint();

    const [passageForm] = Form.useForm();

    const [overallForm] = Form.useForm();

    const { state, resetState, selectMeasure } = useContext(ConfiguratorContext);

    const selectedProduct = state.choice.product;

    if (!selectedProduct) {
        resetState()
        return null;
    }

    const onSubmit = (values) => {
        selectMeasure(values);
        setQueryStringValuesWithoutNavigation({
            width: values.width,
            height: values.height,
            type: values.measureType,
            step: 2
        });
    }

    const onFieldChange = ({ name, value, form }) => {

        const widthDiff = selectedProduct.min_overall_width - selectedProduct.min_passage_width;
        const heightDiff = selectedProduct.min_overall_height - selectedProduct.min_passage_height;

        const relativeDiff = name === 'width' ? widthDiff : heightDiff

        if (form === 'overall') {
            passageForm.setFieldsValue({ [name]: value - relativeDiff });
        } else if (form === 'passage') {
            overallForm.setFieldsValue({ [name]: value + relativeDiff });
        }
    }

    return (
        <div>
            <PageHeader
                title={t('configurator.select_measure')}
                subTitle={t('configurator.select_measure_subtitle')}
                extra={<Button icon={<UndoOutlined />} type="default" onClick={() => resetState()}>{t('configurator.cancel')}</Button>}
            />
            <Row gutter={[24, 24]}>

                <Col xs={24} lg={10} xl={12} xxl={12}>
                    <ProductCard product={selectedProduct} hideProductInfo={true} />
                </Col>

                <Col xs={24} lg={14} xl={12} xxl={12}>

                    <Space
                        direction={screens.lg ? "horizontal" : "vertical"}
                        size="large"
                        style={{ width: "100%" }}
                    >
                        <Card
                            cover={
                                <img
                                    src="/build/images/configurator/passage-size-masonry.jpg"
                                    alt={t('configurator.passage')}
                                />
                            }>
                            <Typography.Title className="mt-1 mb-4" level={4}>{t('configurator.passage')}</Typography.Title>
                            <SizeForm onFieldChange={onFieldChange} form={passageForm} product={selectedProduct} type="passage" onSubmit={onSubmit} t={t} />

                            <Divider />

                            <span>

                                <Trans i18nKey='configurator.min_max_width'
                                    values={{ min: selectedProduct.min_passage_width, max: selectedProduct.max_passage_width }}
                                    components={[<span className='primary-color' />]}
                                />
                            </span>
                            <br />

                            <span>
                                <Trans i18nKey='configurator.min_max_height'
                                    values={{ min: selectedProduct.min_passage_height, max: selectedProduct.max_passage_height }}
                                    components={[<span className='primary-color' />]}
                                />
                            </span>
                        </Card>
                        <Card
                            cover={
                                <img
                                    src="/build/images/configurator/overall-size-masonry.jpg"
                                    alt={t('configurator.overall')}
                                />
                            }
                        >
                            <Typography.Title className="mt-1 mb-4" level={4}>{t('configurator.overall')}</Typography.Title>
                            <SizeForm onFieldChange={onFieldChange} form={overallForm} product={selectedProduct} type="overall" onSubmit={onSubmit} t={t} />

                            <Divider />

                            <span>

                                <Trans i18nKey='configurator.min_max_width'
                                    values={{ min: selectedProduct.min_overall_width, max: selectedProduct.max_overall_width }}
                                    components={[<span className='primary-color' />]}
                                />
                            </span>
                            <br />

                            <span>
                                <Trans i18nKey='configurator.min_max_height'
                                    values={{ min: selectedProduct.min_overall_height, max: selectedProduct.max_overall_height }}
                                    components={[<span className='primary-color' />]}
                                />
                            </span>
                        </Card>

                    </Space>

                    <Alert
                        title={t('configurator.note')}
                        description={t('configurator.measure_note')}
                        type="info"
                        showIcon
                        style={{ marginTop: 24 }}
                    />

                </Col>
            </Row>
        </div>
    );
}

export default MeasureStep;